import { useState, useEffect, useRef } from 'react'
import css from './filters.module.scss'
import { resolutionsFilters, dyeFilters, secondaryAntibodiesFilters, primaryTypeFilters, primaryVariantTypeFilters } from '../../../utils/proteinName'
import { Input } from "../../../componentsV1/Input"
import { Checkbox } from "../../../componentsV1/Checkbox"

// Collapsible filter section helper to avoid JSX markup duplication
const FilterSection = ({
  title,
  isOpen,
  onToggle,
  onClear,
  hasSelectedOptions,
  selectedCount,
  children
}) => {
  return (
    <div className={css.filterTypes}>
      <div className={css.filterTypeHeader} style={{ cursor: 'pointer' }} onClick={onToggle}>
        <h4>
          {title} {selectedCount > 0 && <span>{selectedCount}</span>}
        </h4>
        <div style={{ display: 'flex', gap: '10px' }} onClick={(e) => e.stopPropagation()}>
          {hasSelectedOptions && (
            <i
              className='far fa-times closeIcon'
              onClick={onClear}
            />
          )}
          <i
            className={`far ${isOpen ? 'fa-angle-up' : 'fa-angle-down'} arrowIcon`}
            onClick={onToggle}
          />
        </div>
      </div>
      {isOpen && children}
    </div>
  )
}

export const Filters = ({
  setFilters,
  filters,
  resetFilters,
  setCurrentPage,
  setOpenFilter,
  setIsLoading,
  productType,
  proteinsList
}) => {
  const dropdownRef = useRef(null)
  const [proteinSearch, setProteinSearch] = useState('')
  const [tempProteinsList, setTempProteinsList] = useState([])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenFilter(false)
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setOpenFilter]);

  useEffect(() => {
    if (proteinSearch) {
      const filteredOptions = proteinsList.filter((option) => {
        return option.toLowerCase().includes(proteinSearch.toLowerCase())
      })
      setTempProteinsList(filteredOptions)
    } else {
      setTempProteinsList(proteinsList)
    }
  }, [proteinsList, proteinSearch])

  // Toggle open/close state of a filter dropdown category
  const toggleSection = (key) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [key]: {
        ...prevFilters[key],
        dropDown: !prevFilters[key].dropDown
      }
    }))
  }

  // Toggle multi-select option (arrays)
  const toggleMultiSelect = (key, value) => {
    setFilters(prevFilters => {
      const isSelected = prevFilters[key].selectedOptions.includes(value);
      const newOptions = isSelected
        ? prevFilters[key].selectedOptions.filter(item => item !== value)
        : [...prevFilters[key].selectedOptions, value];
      return {
        ...prevFilters,
        [key]: {
          ...prevFilters[key],
          selectedOptions: newOptions
        }
      }
    })
    setCurrentPage(0)
  }

  // Toggle single-select option (strings)
  const toggleSingleSelect = (key, value) => {
    setFilters(prevFilters => {
      const isSelected = prevFilters[key].selectedOptions === value;
      return {
        ...prevFilters,
        [key]: {
          ...prevFilters[key],
          selectedOptions: isSelected ? '' : value
        }
      }
    })
    setCurrentPage(0)
  }

  // Clear specific filter category
  const clearSection = (key, emptyVal = []) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [key]: {
        ...prevFilters[key],
        selectedOptions: emptyVal
      }
    }))
    setCurrentPage(0)
  }

  const getSelectedCount = (field) => {
    if (Array.isArray(field.selectedOptions)) {
      return field.selectedOptions.length;
    } else if (typeof field.selectedOptions === 'string' && field.selectedOptions.trim()) {
      return 1;
    }
    return 0;
  }

  const totalSelectedFilters = Object.values(filters).reduce((total, field) => total + getSelectedCount(field), 0);
  const anyFilterSelected = Object.values(filters).some(filter => getSelectedCount(filter) > 0);
  console.log(anyFilterSelected)

  return (
    <div className={css.filterDropdown} ref={dropdownRef}>
      <div className={css.filterDropdownContainer}>
        <div className={css.filterHeader}>
          <h3>Filters</h3>
            <h6 onClick={() => setOpenFilter(false)} style={{cursor: 'pointer'}}>
              <i className='far fa-times closeIcon' />
            </h6>
        </div>

        {/* 1. Protein Target */}
        {productType !== 'alexafluor' && (
          <FilterSection
            title="Protein Target"
            isOpen={filters.proteinTarget.dropDown}
            onToggle={() => toggleSection('proteinTarget')}
            onClear={() => {
              clearSection('proteinTarget');
              setProteinSearch('');
            }}
            hasSelectedOptions={filters.proteinTarget.selectedOptions.length > 0}
            selectedCount={filters.proteinTarget.selectedOptions.length}
          >
            <Input
              type='text'
              placeholder='Search'
              clear={proteinSearch}
              onClear={() => setProteinSearch('')}
              search
              style={{ width: '100%', height: '40px', paddingLeft: '30px' }}
              value={proteinSearch}
              onChange={(e) => setProteinSearch(e.target.value)}
            />
            <div className={css.filterTypeContainer} style={{ maxHeight: tempProteinsList.length > 0 ? '300px' : '300px' }}>
              {tempProteinsList.length > 0 ? (
                tempProteinsList.map((item) => (
                  <div className={css.twoColumn} key={item}>
                    <Checkbox
                      label={item}
                      value={item}
                      checked={filters.proteinTarget.selectedOptions.includes(item)}
                      onChange={() => toggleMultiSelect('proteinTarget', item)}
                    />
                  </div>
                ))
              ) : (
                <div className='no-orders'>
                  <span className='icon'>
                    <i className="fal fa-boxes" />
                  </span>
                  <p style={{ color: '#6c6a73', fontWeight: 400, fontSize: '16px' }}>
                    There are no results. <br />Try to broaden your search.
                  </p>
                </div>
              )}
            </div>
          </FilterSection>
        )}

        {/* 2. Primary Type */}
        {!['alexafluor'].includes(productType) && (
          <FilterSection
            title="Primary Type"
            isOpen={filters.primaryType.dropDown}
            onToggle={() => toggleSection('primaryType')}
            onClear={() => clearSection('primaryType', '')}
            hasSelectedOptions={!!filters.primaryType.selectedOptions}
            selectedCount={filters.primaryType.selectedOptions ? 1 : 0}
          >
            <div className={css.filterTypeContainer}>
              {primaryTypeFilters.map(item => (
                <div className={css.twoColumn} key={item.value}>
                  <Checkbox
                    label={item.label}
                    value={item.value}
                    checked={filters.primaryType.selectedOptions === item.value}
                    onChange={() => toggleSingleSelect('primaryType', item.value)}
                  />
                </div>
              ))}
            </div>
          </FilterSection>
        )}

        {/* 3. Fc */}
        {!['alexafluor'].includes(productType) && (
          <FilterSection
            title="Fc"
            isOpen={filters.primaryVariantType.dropDown}
            onToggle={() => toggleSection('primaryVariantType')}
            onClear={() => clearSection('primaryVariantType', '')}
            hasSelectedOptions={!!filters.primaryVariantType.selectedOptions}
            selectedCount={filters.primaryVariantType.selectedOptions ? 1 : 0}
          >
            <div className={css.filterTypeContainer}>
              {primaryVariantTypeFilters.map(item => (
                <div className={css.twoColumn} key={item.value}>
                  <Checkbox
                    label={item.label}
                    value={item.value}
                    checked={filters.primaryVariantType.selectedOptions === item.value}
                    onChange={() => toggleSingleSelect('primaryVariantType', item.value)}
                  />
                </div>
              ))}
            </div>
          </FilterSection>
        )}

        {/* 4. Conjugate Type */}
        {productType === 'primarys' && (
          <FilterSection
            title="Conjugate Type"
            isOpen={filters.conjugateType.dropDown}
            onToggle={() => toggleSection('conjugateType')}
            onClear={() => clearSection('conjugateType')}
            hasSelectedOptions={filters.conjugateType.selectedOptions.length > 0}
            selectedCount={filters.conjugateType.selectedOptions.length}
          >
            <div className={css.filterTypeContainer}>
              <div className={css.twoColumn}>
                <Checkbox
                  label='Primary Conjugate'
                  value='Primary Conjugate'
                  checked={filters.conjugateType.selectedOptions.includes('Primary Conjugate')}
                  onChange={() => toggleMultiSelect('conjugateType', 'Primary Conjugate')}
                />
              </div>
              <div className={css.twoColumn}>
                <Checkbox
                  label='Secondary Conjugate'
                  value='Secondary Conjugate'
                  checked={filters.conjugateType.selectedOptions.includes('Secondary Conjugate')}
                  onChange={() => toggleMultiSelect('conjugateType', 'Secondary Conjugate')}
                />
              </div>
            </div>
          </FilterSection>
        )}

        {/* 5. Dye */}
        {productType !== 'primary' && (
          <FilterSection
            title="Dye"
            isOpen={filters.dye.dropDown}
            onToggle={() => toggleSection('dye')}
            onClear={() => clearSection('dye')}
            hasSelectedOptions={filters.dye.selectedOptions.length > 0}
            selectedCount={filters.dye.selectedOptions.length}
          >
            <div className={css.filterTypeContainer}>
              {dyeFilters.map(item => {
                if (productType === 'western' && item.value === 'Control') return null
                return (
                  <div className={css.twoColumn} key={item.value}>
                    <Checkbox
                      label={item.label}
                      value={item.value}
                      checked={filters.dye.selectedOptions.includes(item.value)}
                      onChange={() => toggleMultiSelect('dye', item.value)}
                    />
                  </div>
                )
              })}
            </div>
          </FilterSection>
        )}

        {/* 6. Resolution */}
        {productType !== 'western' && (
          <FilterSection
            title="Resolution"
            isOpen={filters.resolution.dropDown}
            onToggle={() => toggleSection('resolution')}
            onClear={() => clearSection('resolution')}
            hasSelectedOptions={filters.resolution.selectedOptions.length > 0}
            selectedCount={filters.resolution.selectedOptions.length}
          >
            <div className={css.filterTypeContainer}>
              {resolutionsFilters.map(item => (
                <div className={css.twoColumn} key={item.value}>
                  <Checkbox
                    label={item.label}
                    value={item.value}
                    checked={filters.resolution.selectedOptions.includes(item.value)}
                    onChange={() => toggleMultiSelect('resolution', item.value)}
                  />
                </div>
              ))}
            </div>
          </FilterSection>
        )}

        {/* 7. Secondary Antibodies */}
        {(productType === 'alexafluor' || productType === 'western') && (
          <FilterSection
            title="Secondary Antibodies"
            isOpen={filters.secondaryAntibodies.dropDown}
            onToggle={() => toggleSection('secondaryAntibodies')}
            onClear={() => clearSection('secondaryAntibodies')}
            hasSelectedOptions={filters.secondaryAntibodies.selectedOptions.length > 0}
            selectedCount={filters.secondaryAntibodies.selectedOptions.length}
          >
            <div className={css.filterTypeContainer} style={{ gap: '16px' }}>
              {secondaryAntibodiesFilters.map(item => (
                <div className={css.subfilterTypeContainer} key={item.name}>
                  <h4>{item.name}</h4>
                  {item.subFilters?.map(subItem => (
                    <div className={css.twoColumn} key={subItem.value}>
                      <Checkbox
                        label={subItem.label}
                        value={subItem.value}
                        checked={filters.secondaryAntibodies.selectedOptions.includes(subItem.value)}
                        onChange={() => toggleMultiSelect('secondaryAntibodies', subItem.value)}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </FilterSection>
        )}
      </div>
      <hr style={{ height: '1px', background: '#EDEDED' }} />
      <div className={css.filterBtnContainer}>
        <button className={`${css.cancelBtn} ${css.btn}`} 
        onClick={() => {
          resetFilters()
          setOpenFilter(false)
        }}>Clear Filters</button>
        <button className={css.btn} disabled={anyFilterSelected ? false : true} onClick={() => setOpenFilter(false)}>
          Apply {totalSelectedFilters > 0 ? `(${totalSelectedFilters})` : ''}
        </button>
      </div>
    </div>
  )
}